import { jsx as _jsx } from "react/jsx-runtime";
import { getZombieInstanceFromPrimaryKey } from '@iotv/datamodel';
import queryString from 'query-string';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { ThingViewArbitrator } from '../../ArbitrationObjects/ThingViewArbitrator';
import Assembler from '../../data/Assembler';
import { getAdjacent2Wrapper, getOne } from '../../data/daoFunctions/daoFunctions';
import { isViewOnly } from '../../helpers/access';
import { AdjacentType, ComponentMode, ObjectViewType, UserTransactionType, } from '../../types/AppTypes';
import { getViewObjectHash, inflateViewObject } from '../../util/AppData/viewObject';
import { modelTypes } from '../../util/Model/modelTypes';
import ObjectCard from '../../v2/components/generic/ObjectView/ObjectCard';
const debug = process.env.REACT_APP_DEBUG && false;
function ThingView({ thingViewMap, viewObject, transactionFunctions, userFunctions, location, contextCustomer, user, useSpecializedViews, userSelectedGroup, userGroupRoles, applicationData, roleArbitrationInstance, isSmallView, ...rest }) {
    const params = useParams();
    const { type, id } = params;
    const sk = `${type}:${id}`;
    debug && console.log('ThingView got from container', { user, thingViewMap, params, viewObject, isSmallView });
    const displayMessage = (content, type) => userFunctions.setUserMessage({ id: uuidv4(), content, label: content, type });
    const getNotInStateObject = async (params) => {
        const itemRes = await getOne(params.scopeDefinitingContextObject);
        if (itemRes.data) {
            let items = [itemRes.data];
            const adjacentRes = await getAdjacent2Wrapper(params);
            debug && console.log('getNotInStateObject got', adjacentRes);
            if (adjacentRes.data?.Items)
                items = [...items, ...adjacentRes.data.Items];
            else if (adjacentRes.err)
                displayMessage(adjacentRes.err.message, 'MessageBar');
            const viewObject = inflateViewObject(items, params.scopeDefinitingContextObject);
            if (viewObject.matchedPrimary)
                viewObject.matchedPrimary = Assembler.getInstance(viewObject.matchedPrimary);
            setPossibleViewObject(viewObject);
            userFunctions.addObjectsToState(items);
        }
    };
    const getNotInstanceObjectFromParams = () => {
        if (type && id && modelTypes.includes(type)) {
            debug && console.log('ThingView getting something that is not in state', sk);
            const newInstance = getZombieInstanceFromPrimaryKey(sk);
            const params = { adjacencyType: AdjacentType.CHILD, objectTypeId: '*', scopeDefinitingContextObject: newInstance, includeEdges: true };
            getNotInStateObject(params);
        }
    };
    const viewObjectHash = getViewObjectHash(viewObject);
    const searchParams = location?.search ? queryString.parse(location.search) : undefined;
    const [possibleViewObject, setPossibleViewObject] = React.useState(viewObject);
    useEffect(() => {
        const isNewObjectAction = searchParams?.action &&
            [UserTransactionType.CREATE, UserTransactionType.CREATE_AND_LINK, UserTransactionType.REGISTER].includes(searchParams.action);
        debug && console.log('ThingView isNewObjectAction', isNewObjectAction);
        /** this new object clause is probably redundant ( 2025 01) */
        if (possibleViewObject?.matchedPrimary === undefined && isNewObjectAction) {
            let matchedPrimary = undefined;
            const { type, id } = params;
            if (searchParams?.objectTypeId && searchParams?.objectUUID) {
                matchedPrimary = Assembler.getInstance({ type: searchParams.objectTypeId, id: searchParams.objectUUID });
                debug && console.log('ThingView, With No Matched Primary but objectTypeId and objectUUID, Assember returned', matchedPrimary);
            }
            else if (type && id) {
                matchedPrimary = Assembler.getInstance({ type, id });
                debug && console.log('ThingView, With No Matched Primary and objectTypeId and objectUUID, Assember returned', matchedPrimary);
            }
            setPossibleViewObject({ matchedPrimary, matchedRelatedByPk: [], matchedRelatedBySk: [] });
        }
        else if (possibleViewObject?.matchedPrimary && viewObject.matchedPrimary) {
            debug && console.log('ThingView With a possibleViewObject Matched Primary, ', possibleViewObject);
            const matchedPrimary = Assembler.getInstance(viewObject.matchedPrimary);
            setPossibleViewObject({ ...viewObject, matchedPrimary });
            debug && console.log('ThingView Assember returned', matchedPrimary);
        }
        else if (possibleViewObject?.matchedPrimary && !viewObject.matchedPrimary) {
            console.warn('ThingView Reached known error state where no viewObject.matchedPrimary', { possibleViewObject, viewObject });
            // const matchedPrimary = Assembler.getInstance(viewObject.matchedPrimary as ValidBusinessObject);
            // setPossibleViewObject( { ...viewObject, matchedPrimary})
            // debug && console.log('Assember returned', matchedPrimary)
            getNotInstanceObjectFromParams();
        }
        else {
            if (type && id) {
                getNotInstanceObjectFromParams();
            }
            else {
                debug &&
                    console.log(`ThingView fell through useEffect options, with type ${type}. ${type} is in modelTypes? ${modelTypes.includes(type ?? 'never')}`, {
                        modelTypes,
                    });
            }
        }
        return () => {
            setPossibleViewObject(undefined);
        };
    }, [viewObjectHash, location.pathname]);
    debug && console.log('ThingView actual viewObject', { viewObject, searchParams, user, location });
    let SelectedClass = null;
    if (params.type && thingViewMap) {
        const thingsViewArbitratorInput = {
            thingViewMap,
            objectTypeId: params.type,
            objectViewType: ObjectViewType.Item,
            useSpecializedViews,
            groupSupportedType: viewObject.matchedPrimary?.supportedType,
        };
        SelectedClass = ThingViewArbitrator(thingsViewArbitratorInput);
        debug && console.log('ThingViewArbitrator Selected Class', SelectedClass);
    }
    /** @deprecated */
    const initEditMode = isViewOnly(userSelectedGroup) ? ComponentMode.VIEW : ComponentMode.EDIT;
    const viewedType = params.type;
    const singleObjectAllowedActions = roleArbitrationInstance?.getSingleObjectControlActions(viewedType);
    debug && console.log('ThingView', `allowed actions for ${roleArbitrationInstance?.groupName} viewing ${viewedType}`, singleObjectAllowedActions);
    const SelectedComponent = SelectedClass ? (_jsx(SelectedClass, { viewObject,
        contextCustomer,
        user,
        userGroupRoles,
        userSelectedGroup,
        userFunctions,
        transactionFunctions,
        initialEditMode: initEditMode,
        useSpecializedViews,
        applicationData,
        location,
        roleArbitrationInstance,
        allowedActions: singleObjectAllowedActions,
        isSmallView,
        ...rest }, sk)) : null;
    return SelectedComponent ?? _jsx(ObjectCard, { transactionFunctions, viewObject, allowedActions: singleObjectAllowedActions, isSmallView }, sk);
}
export default ThingView;
